import { useState, useCallback } from 'react';

interface ConfirmationState {
  isOpen: boolean;
  title: string;
  message: string;
  confirmText: string;
  cancelText: string | null;
  type: 'danger' | 'warning' | 'info';
  onConfirm: () => void;
  onCancel: () => void;
  isLoading: boolean;
  isSuccess: boolean;
  successMessage: string;
}

const initialState: ConfirmationState = {
  isOpen: false,
  title: '',
  message: '',
  confirmText: 'Confirm',
  cancelText: 'Cancel' as string | null,
  type: 'warning',
  onConfirm: () => {},
  onCancel: () => {},
  isLoading: false,
  isSuccess: false,
  successMessage: '',
};

export const useConfirmation = () => {
  const [confirmation, setConfirmation] = useState<ConfirmationState>(initialState);

  const showConfirmation = useCallback((options: {
    title: string;
    message: string;
    confirmText?: string;
    cancelText?: string | null;
    type?: 'danger' | 'warning' | 'info';
    onConfirm: () => void;
    onCancel?: () => void;
  }) => {
    setConfirmation({
      isOpen: true,
      title: options.title,
      message: options.message,
      confirmText: options.confirmText || 'Confirm',
      cancelText: options.cancelText !== null ? (options.cancelText || 'Cancel') : null,
      type: options.type || 'warning',
      onConfirm: () => {
        options.onConfirm();
      },
      onCancel: () => {
        options.onCancel?.();
        setConfirmation(initialState);
      },
      isLoading: false,
      isSuccess: false,
      successMessage: '',
    });
  }, []);

  const hideConfirmation = useCallback(() => {
    setConfirmation(initialState);
  }, []);

  const setLoading = useCallback((loading: boolean) => {
    setConfirmation(prev => ({ ...prev, isLoading: loading }));
  }, []);

  const setSuccess = useCallback((message: string) => {
    setConfirmation(prev => ({ 
      ...prev, 
      isLoading: false, 
      isSuccess: true, 
      successMessage: message 
    }));
    
    // Auto-close after 2 seconds
    setTimeout(() => {
      setConfirmation(initialState);
    }, 2000);
  }, []);

  return {
    confirmation,
    showConfirmation,
    hideConfirmation,
    setLoading,
    setSuccess,
  };
}; 